import type { ScanReviewGroup } from "@hobbyshopify/backend-client";
import { Link } from "react-router-dom";
import { Obs, useObserver, useSubtree } from "subtree.js/react";
import { BulkDropZone } from "../../components/BulkDropZone";
import { InventoryTabs } from "../../components/InventoryTabs";
import { LoadFailed, Loading } from "../../components/LoadState";
import { ReviewGroupRow } from "../../components/ReviewGroupRow";
import { ScanningScanRow } from "../../components/ScanningScanRow";
import { UnrecognisedScan } from "../../components/UnrecognisedScan";
import {
  batchEntry,
  isCustomerGroup,
  reviewEntry,
  reviewPriceText,
  reviewQuantity,
} from "../../core/primitives/review-row";
import { CONFIRM_ALL_KEY, DraftsActions, DraftsState } from "./drafts-model";

export function DraftsPage() {
  const state = useSubtree(DraftsState);
  const actions = useSubtree(DraftsActions);
  const observer = useObserver();

  const status = observer.watch(state.status);
  const groups = observer.watch(state.groups);
  const unrecognised = observer.watch(state.unrecognised);
  const busyKey = observer.watch(state.busyKey);
  const actionError = observer.watch(state.actionError);
  const bulkOpen = observer.watch(state.bulkOpen);
  const bulkItems = observer.watch(state.bulkItems);
  const drafts = observer.watch(state.drafts);
  const picking = observer.watch(state.picking);
  const pricedCount = groups.filter((g) => batchEntry(g, drafts[g.key]) !== null).length;

  const loading = (status.kind === "idle" || status.kind === "loading") && groups.length === 0;
  const error = status.kind === "error";

  const newCards = groups.filter((g) => g.bucket === "new");
  const existing = groups.filter((g) => g.bucket === "exists");

  const renderGroups = (list: readonly ScanReviewGroup[]) =>
    list.map((group) => (
      <ReviewGroupRow
        key={group.key}
        group={group}
        busy={busyKey === group.key || busyKey === CONFIRM_ALL_KEY}
        price={reviewPriceText(group, drafts[group.key])}
        quantity={reviewQuantity(group, drafts[group.key])}
        showQuantity={!isCustomerGroup(group)}
        canConfirm={reviewEntry(group, drafts[group.key]) !== null}
        pickingProductId={picking?.key === group.key ? picking.productId : null}
        onPrice={(price) => actions.setPrice(group, price)}
        onQuantity={(quantity) => actions.setQuantity(group, quantity)}
        onPick={(productId) => void actions.pick(group, productId)}
        onConfirm={() => void actions.confirm(group)}
        onReview={() => actions.review(group)}
        onReject={() => void actions.reject(group)}
      />
    ));

  return (
    <div className="mx-auto max-w-7xl px-2 py-4 sm:px-6 sm:py-8 lg:px-8">
      <div className="panel p-4 sm:p-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <h1 className="text-lg font-bold text-white">Inventory</h1>
          <InventoryTabs active="review" reviewCount={groups.reduce((n, g) => n + g.count, 0)} />
          <div className="flex gap-2">
            {/* A toggle, so the open state fills: the accent reads as "this mode
                is on", not merely as "press me". */}
            <button
              type="button"
              onClick={actions.toggleBulk}
              aria-expanded={bulkOpen}
              className={`btn-sm ${bulkOpen ? "btn-primary" : "btn-secondary"}`}
            >
              Bulk Scan
            </button>
            <Link to="/scan" className="btn-primary btn-sm">
              New Scan
            </Link>
          </div>
        </div>

        {bulkOpen && (
          <Obs>
            {(ref) => {
              const counts = ref.watch(state.bulkCounts);
              return (
                <BulkDropZone
                  onFiles={actions.addFiles}
                  onStop={actions.stopBulk}
                  onResume={actions.resumeBulk}
                  running={ref.watch(state.bulkRunning)}
                  paused={ref.watch(state.bulkPaused)}
                  queued={counts.queued}
                  scanning={counts.scanning}
                />
              );
            }}
          </Obs>
        )}

        {actionError && (
          <div className="note note-danger mb-4 flex items-center justify-between gap-3 text-danger">
            <span className="truncate">{actionError}</span>
            <button type="button" onClick={actions.dismissError} className="btn-ghost shrink-0">
              Dismiss
            </button>
          </div>
        )}

        {status.kind === "error" && (
          <LoadFailed title="Couldn't load the review queue." message={status.message} onRetry={actions.retry} />
        )}

        {loading ? (
          <Loading label="Loading the review queue…" />
        ) : error && groups.length === 0 ? null : bulkItems.length === 0 && groups.length === 0 ? (
          <div className="py-10 text-center">
            <p className="mb-4 text-sm text-muted">Nothing waiting for review.</p>
            <Link to="/scan" className="btn-primary btn-sm">
              Start a scan
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {groups.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-row border border-line bg-surface-strong px-4 py-3">
                <p className="text-sm text-muted">
                  {groups.length} waiting · {pricedCount} ready to confirm
                </p>
                <button
                  type="button"
                  onClick={() => void actions.confirmAllPriced()}
                  disabled={busyKey !== null || pricedCount === 0}
                  className="btn-primary btn-sm"
                >
                  {busyKey === CONFIRM_ALL_KEY ? "Confirming…" : `Confirm all priced (${pricedCount})`}
                </button>
              </div>
            )}

            {bulkItems.length > 0 && (
              <div className="space-y-2">
                {bulkItems.map((item) => (
                  <ScanningScanRow
                    key={item.id}
                    item={item}
                    onRetry={() => actions.retryItem(item.id)}
                    onRemove={() => actions.removeItem(item.id)}
                  />
                ))}
              </div>
            )}

            {newCards.length > 0 && (
              <section className="space-y-2">
                <h2 className="text-sm font-semibold text-white">
                  New cards <span className="text-faint">({newCards.length})</span>
                </h2>
                <p className="text-xs text-muted">
                  The matcher found nothing in your catalogue. Confirming one creates the card.
                </p>
                {renderGroups(newCards)}
              </section>
            )}

            {existing.length > 0 && (
              <section className="space-y-2">
                <h2 className="text-sm font-semibold text-white">
                  Already in your catalogue <span className="text-faint">({existing.length})</span>
                </h2>
                <p className="text-xs text-muted">
                  Each of these matched a card you already deal in. Confirming adds stock to it.
                </p>
                {renderGroups(existing)}
              </section>
            )}
          </div>
        )}
        {unrecognised.length > 0 && (
          <section className="mt-8 space-y-2">
            <h2 className="text-base font-semibold text-white">Unrecognised scans ({unrecognised.length})</h2>
            {unrecognised.map((scan) => (
              <UnrecognisedScan key={scan.id} scan={scan} />
            ))}
          </section>
        )}
      </div>
    </div>
  );
}
